import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  List,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IssueOut, ProductRef, UpdDocumentDetail, Uom } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { ProductSelect } from '@/components/ProductSelect';
import { QtyInput } from '@/components/qty';
import { formatDecimal, fromScaled, toScaled } from '@/lib/decimal';
import { InvalidQty, type QtyValue } from '@/lib/qty';
import { blankToNull } from '@/lib/text';
import { uomLabel } from '@/lib/units';
import { useApplyResolution } from '@/pages/upd/useApplyResolution';

// Данные проблемы — по коду (backend/app/upd/resolve_client.py, resolve_lines.py).
interface ClientData {
  suggestion: { name: string; inn: string | null; kpp: string | null; address: string | null };
}
interface PointCandidate {
  id: number;
  name: string;
  address: string | null;
  score: number;
}
interface PointData {
  consignee: string;
  candidates: PointCandidate[];
  suggestion: { name: string; address: string | null; kpp: string | null };
}
interface ProductCandidate {
  id: number;
  name: string;
  base_uom: Uom;
  matched_alias: string | null;
  score: number;
}
interface ProductData {
  raw_name?: string;
  article?: string | null;
  candidates?: ProductCandidate[];
  suggestion?: { name: string; base_uom: Uom };
  product?: { id: number; name: string; base_uom?: Uom };
}
interface QuantData {
  product: { id: number; name: string };
  article: string | null;
  source: { qty: string; okei: string | null; uom_label: string | null };
  suggestion: { kg_per_pcs: string; origin: string } | null;
  history: { kg_per_pcs: string; client_id: number | null; confirmed_at: string }[];
}

/** Форма решения одной проблемы УПД — по коду проблемы. */
export function IssueResolver({
  doc,
  issue,
  clientPending,
}: {
  doc: UpdDocumentDetail;
  issue: IssueOut;
  /** Покупатель ещё не определён — решения за клиентом недоступны. */
  clientPending: boolean;
}) {
  const { t } = useTranslation();
  if (issue.code === 'CLIENT_UNRESOLVED') return <ClientResolver doc={doc} issue={issue} />;
  if (clientPending)
    return <Typography.Text type="secondary">{t('resolve.clientFirst')}</Typography.Text>;
  switch (issue.code) {
    case 'DELIVERY_POINT_UNRESOLVED':
      return <PointResolver doc={doc} issue={issue} />;
    case 'PRODUCT_UNRESOLVED':
    case 'UOM_INCOMPATIBLE':
      return <ProductResolver doc={doc} issue={issue} />;
    case 'UOM_CONVERSION_REQUIRED':
      return <QuantResolver doc={doc} issue={issue} />;
    default:
      return <Alert type="warning" showIcon message={t('resolve.unknownUnit')} />;
  }
}

function ErrorLine({ text }: { text: string | null }) {
  return text ? <Alert type="error" showIcon message={text} style={{ marginTop: 8 }} /> : null;
}

function ClientResolver({ doc, issue }: { doc: UpdDocumentDetail; issue: IssueOut }) {
  const { t } = useTranslation();
  const s = (issue.data as unknown as ClientData).suggestion;
  const [name, setName] = useState(s.name);
  const [brand, setBrand] = useState('');
  const r = useApplyResolution(doc);
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        {t('resolve.client.fromUpd')}: {s.name}
        {s.inn && ` · ИНН ${s.inn}`}
        {s.address && ` · ${s.address}`}
      </Typography.Text>
      <Space wrap>
        <Input
          aria-label={t('resolve.client.name')}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          style={{ width: 360 }}
        />
        <Input
          aria-label={t('resolve.client.brand')}
          placeholder={t('resolve.client.brand')}
          value={brand}
          onChange={(e) => {
            setBrand(e.target.value);
          }}
          style={{ width: 200 }}
        />
        <Button
          type="primary"
          loading={r.pending}
          onClick={() => {
            r.apply({
              issue_id: issue.issue_id,
              type: 'create_client',
              name: blankToNull(name),
              brand: blankToNull(brand),
            });
          }}
        >
          {t('resolve.client.create')}
        </Button>
      </Space>
      <ErrorLine text={r.errorText} />
    </Space>
  );
}

function PointResolver({ doc, issue }: { doc: UpdDocumentDetail; issue: IssueOut }) {
  const { t } = useTranslation();
  const d = issue.data as unknown as PointData;
  const [choice, setChoice] = useState<number | 'new'>(d.candidates[0]?.id ?? 'new');
  const [remember, setRemember] = useState(true);
  const [form] = Form.useForm<{ name: string; address?: string; kpp?: string }>();
  const r = useApplyResolution(doc);
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        {t('resolve.point.consignee')}: {d.consignee}
      </Typography.Text>
      <Radio.Group
        value={choice}
        onChange={(e) => {
          setChoice(e.target.value as number | 'new');
        }}
      >
        <Space direction="vertical">
          {d.candidates.map((c) => (
            <Radio key={c.id} value={c.id}>
              {t('resolve.point.candidate', { name: c.name })}
              {c.address && <Typography.Text type="secondary"> · {c.address}</Typography.Text>}
            </Radio>
          ))}
          <Radio value="new">{t('resolve.point.newPoint')}</Radio>
        </Space>
      </Radio.Group>
      {choice === 'new' ? (
        <Form
          form={form}
          layout="inline"
          initialValues={{
            name: d.suggestion.name,
            address: d.suggestion.address ?? '',
            kpp: d.suggestion.kpp ?? '',
          }}
          onFinish={(v) => {
            r.apply({
              issue_id: issue.issue_id,
              type: 'create_delivery_point',
              name: v.name.trim(),
              address: blankToNull(v.address),
              kpp: blankToNull(v.kpp),
            });
          }}
        >
          <Form.Item name="name" label={t('resolve.point.name')} rules={[{ required: true }]}>
            <Input style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="address" label={t('resolve.point.address')}>
            <Input style={{ width: 320 }} />
          </Form.Item>
          <Form.Item name="kpp" label={t('resolve.point.kpp')}>
            <Input style={{ width: 120 }} maxLength={9} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={r.pending}>
            {t('resolve.apply')}
          </Button>
        </Form>
      ) : (
        <Space wrap>
          <Checkbox
            checked={remember}
            onChange={(e) => {
              setRemember(e.target.checked);
            }}
          >
            {t('resolve.point.remember')}
          </Checkbox>
          <Button
            type="primary"
            loading={r.pending}
            onClick={() => {
              r.apply({
                issue_id: issue.issue_id,
                type: 'choose_delivery_point',
                delivery_point_id: choice,
                remember,
              });
            }}
          >
            {t('resolve.apply')}
          </Button>
        </Space>
      )}
      <ErrorLine text={r.errorText} />
    </Space>
  );
}

type Remember = 'client' | 'global' | 'none';

function ProductResolver({ doc, issue }: { doc: UpdDocumentDetail; issue: IssueOut }) {
  const { t } = useTranslation();
  const d = issue.data as unknown as ProductData;
  const candidates = d.candidates ?? [];
  const [choice, setChoice] = useState<number | 'other' | 'new'>(
    candidates[0]?.id ?? (d.suggestion ? 'new' : 'other'),
  );
  const [other, setOther] = useState<ProductRef | null>(null);
  const [name, setName] = useState(d.suggestion?.name ?? '');
  const [uom, setUom] = useState<Uom>(d.suggestion?.base_uom ?? 'kg');
  const [remember, setRemember] = useState<Remember>('client');
  const r = useApplyResolution(doc);
  const similar = r.error instanceof ApiProblem && r.error.code === 'similar_products';
  const line = doc.parsed?.lines.find((l) => l.line_no === issue.line_no);

  function apply(confirmNew = false) {
    if (choice === 'new') {
      r.apply({
        issue_id: issue.issue_id,
        type: 'create_product',
        name: name.trim(),
        base_uom: uom,
        confirm_new: confirmNew,
        remember,
      });
    } else {
      const productId = choice === 'other' ? other?.id : choice;
      if (productId === undefined) return;
      r.apply({
        issue_id: issue.issue_id,
        type: 'choose_product',
        product_id: productId,
        remember,
      });
    }
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {line && (
        <Typography.Text type="secondary">
          {t('resolve.product.line', { name: line.name })}
          {line.article && ` · ${t('resolve.product.article', { article: line.article })}`}
          {` · ${formatDecimal(line.qty)} ${line.uom_label ?? ''}`}
        </Typography.Text>
      )}
      <Radio.Group
        value={choice}
        onChange={(e) => {
          setChoice(e.target.value as number | 'other' | 'new');
          r.reset();
        }}
      >
        <Space direction="vertical">
          {candidates.map((c) => (
            <Radio key={c.id} value={c.id}>
              {c.name} <Tag>{uomLabel(t, c.base_uom)}</Tag>
              {c.matched_alias && (
                <Typography.Text type="secondary">← {c.matched_alias}</Typography.Text>
              )}
            </Radio>
          ))}
          <Radio value="other">{t('resolve.product.other')}</Radio>
          <Radio value="new">{t('resolve.product.newProduct')}</Radio>
        </Space>
      </Radio.Group>
      {choice === 'other' && (
        <div style={{ maxWidth: 420 }}>
          <ProductSelect
            value={other?.id ?? null}
            onChange={(_, p) => {
              setOther(p);
            }}
          />
        </div>
      )}
      {choice === 'new' && (
        <Space wrap>
          <Input
            aria-label={t('resolve.product.name')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              r.reset();
            }}
            style={{ width: 320 }}
          />
          <Radio.Group
            optionType="button"
            value={uom}
            onChange={(e) => {
              setUom(e.target.value as Uom);
            }}
            options={[
              { value: 'kg', label: t('uom.kgLong') },
              { value: 'pcs', label: t('uom.pcsLong') },
              { value: 'box', label: t('uom.boxLong') },
            ]}
          />
        </Space>
      )}
      <Space wrap>
        <Typography.Text>{t('resolve.product.remember')}:</Typography.Text>
        <Select<Remember>
          value={remember}
          onChange={setRemember}
          style={{ width: 360 }}
          options={[
            { value: 'client', label: t('resolve.product.rememberClient') },
            { value: 'global', label: t('resolve.product.rememberGlobal') },
            { value: 'none', label: t('resolve.product.rememberNone') },
          ]}
        />
        <Button
          type="primary"
          loading={r.pending}
          disabled={(choice === 'other' && !other) || (choice === 'new' && !name.trim())}
          onClick={() => {
            apply();
          }}
        >
          {t('resolve.apply')}
        </Button>
      </Space>
      {similar ? (
        <Alert
          type="warning"
          showIcon
          message={t('resolve.product.similar')}
          action={
            <Button
              size="small"
              danger
              onClick={() => {
                apply(true);
              }}
            >
              {t('resolve.product.createAnyway')}
            </Button>
          }
        />
      ) : (
        <ErrorLine text={r.errorText} />
      )}
    </Space>
  );
}

function QuantResolver({ doc, issue }: { doc: UpdDocumentDetail; issue: IssueOut }) {
  const { t } = useTranslation();
  const d = issue.data as unknown as Partial<QuantData>;
  const [kg, setKg] = useState<QtyValue>(d.suggestion?.kg_per_pcs ?? null);
  const [remember, setRemember] = useState(true);
  const r = useApplyResolution(doc);
  // Данные проблемы приходят с сервера; без них формы нет — но и страница не падает.
  if (!d.product || !d.source) return null;
  const { product, source } = d;
  const total =
    typeof kg === 'string' && /[1-9]/.test(kg)
      ? fromScaled(toScaled(source.qty, 3) * toScaled(kg, 4), 7)
      : null;
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text>{t('resolve.uom.question', { product: product.name })}</Typography.Text>
      <Space wrap align="start">
        <Form.Item
          style={{ margin: 0 }}
          {...(kg instanceof InvalidQty
            ? { validateStatus: 'error' as const, help: kg.message }
            : {})}
        >
          <QtyInput
            aria-label={t('resolve.uom.kg')}
            uom="kg"
            places={4}
            unitLabel={t('uom.kgPerPcs')}
            value={kg}
            onChange={setKg}
            style={{ width: 200 }}
          />
        </Form.Item>
        {total && (
          <Tag color="blue" style={{ fontSize: 14, padding: '4px 8px' }}>
            {t('resolve.uom.preview', {
              pcs: formatDecimal(source.qty),
              kg: formatDecimal(kg as string),
              total: formatDecimal(total, { maxPlaces: 3 }),
            })}
          </Tag>
        )}
      </Space>
      {d.suggestion && (
        <Typography.Text type="secondary">
          {t(`resolve.uom.origin.${d.suggestion.origin}`)}: {formatDecimal(d.suggestion.kg_per_pcs)}
        </Typography.Text>
      )}
      {d.history && d.history.length > 0 && (
        <List
          size="small"
          header={<Typography.Text type="secondary">{t('resolve.uom.history')}</Typography.Text>}
          dataSource={d.history}
          renderItem={(h) => (
            <List.Item>
              {formatDecimal(h.kg_per_pcs)} {t('uom.kgPerPcs')} ·{' '}
              {dayjs(h.confirmed_at).format('DD.MM.YYYY')}
            </List.Item>
          )}
        />
      )}
      <Space wrap>
        <Checkbox
          checked={remember}
          onChange={(e) => {
            setRemember(e.target.checked);
          }}
        >
          {t('resolve.uom.remember')}
        </Checkbox>
        <Button
          type="primary"
          loading={r.pending}
          disabled={total === null}
          onClick={() => {
            if (typeof kg !== 'string') return;
            r.apply({
              issue_id: issue.issue_id,
              type: 'uom_conversion',
              kg_per_pcs: kg,
              remember: remember ? 'client' : 'none',
            });
          }}
        >
          {t('resolve.apply')}
        </Button>
      </Space>
      <ErrorLine text={r.errorText} />
    </Space>
  );
}
